import {Component, EventEmitter, Input, Output} from '@angular/core';
import {MatCardModule} from '@angular/material/card';
import {CommonModule, DatePipe} from '@angular/common';
import {RouterModule} from '@angular/router';
import {MatButtonModule} from '@angular/material/button';
import {MatIconModule} from '@angular/material/icon';
import {DataManagementAppService} from 'src/app/services/data-management-app.service';
import {ShowDialogsComponent} from '../show-dialogs/show-dialogs.component';
import {MatDialog} from '@angular/material/dialog';
import {SharedFeaturesService} from 'src/app/services/shared-features.service';

interface ButtonInfo {
  btnName: string;
  btnDesc?: string;
  btnRoute: string;
  dataName: string;
  fileCount?: number;
  updatedDtm?: string;
}

@Component({
  selector: 'app-data-management-card',
  standalone: true,
  imports: [
    MatCardModule,
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
  ],
  template: `
    <div class="cardgrid" [class.mt-4]="!additionalIconBool">
      <ng-container *ngFor="let button of buttonsKeys; let i = index">
        <!-- Add / Upload dashed card -->
        <a
          *ngIf="additionalIconBool"
          class="dcard addcard"
          [routerLink]="button.btnRoute + button.dataName"
          (click)="setDataName(button.dataName)"
        >
          <span class="circle">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 5v14M5 12h14"/></svg>
          </span>
          {{ button.btnName }}
        </a>

        <!-- Dataset card -->
        <div *ngIf="!additionalIconBool" class="dcard">
          <a
            class="dcard-body"
            [routerLink]="button.btnRoute + button.dataName"
            (click)="setDataName(button.dataName)"
          >
            <span class="fileico">
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z"/><path d="M14 3v5h5"/></svg>
            </span>
            <div class="nm">{{ button.btnName }}</div>
            <span class="sub" *ngIf="getSubtitle(button) as sub">{{ sub }}</span>
          </a>
          <div class="foot">
            <button
              type="button"
              class="iconbtn dl"
              *ngIf="isFileUploadDataset(button)"
              (click)="downloadDataset(button)"
              aria-label="Download dataset files"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v12M7 10l5 5 5-5"/><path d="M5 21h14"/></svg>
            </button>
            <button
              type="button"
              class="iconbtn danger"
              (click)="deleteDataset(button.dataName, button.btnRoute, i)"
              aria-label="Delete dataset"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M6 6l1 14h10l1-14"/><path d="M10 11v5M14 11v5"/></svg>
            </button>
          </div>
        </div>
      </ng-container>
    </div>
  `,
  styleUrls: ['./data-management-card.component.scss'],
  providers: [DatePipe],
})
export class DataManagementCardComponent {
  @Input() tabName: string = 'title';
  @Input() tabRoute: string = '';
  @Input() buttonsKeys: ButtonInfo[] = [];
  @Input() tabDesc: string = 'desc';
  @Output() datasetName = new EventEmitter<string>();
  @Input() additionalIconBool: boolean = false;

  constructor(
    private dataManagementService: DataManagementAppService,
    private dialog: MatDialog,
    private snackBar: SharedFeaturesService,
    private datePipe: DatePipe
  ) {
  }

  setDataName(data: string) {
    this.datasetName.emit(data);
  }

  isBtnNameAdd(value: string) {
    return value == 'Add New Data';
  }

  getSubtitle(button: ButtonInfo): string {
    const hasCount = typeof button.fileCount === 'number';
    const hasDate = !!button.updatedDtm;
    const when = hasDate
      ? this.datePipe.transform(button.updatedDtm, 'mediumDate')
      : '';

    if (hasCount) {
      let filesText: string;
      if (button.fileCount === 0) {
        filesText = 'No files yet';
      } else if (button.fileCount === 1) {
        filesText = '1 file';
      } else {
        filesText = `${button.fileCount} files`;
      }
      return hasDate ? `${filesText} · updated ${when}` : filesText;
    }

    if (hasDate) {
      return `Updated ${when}`;
    }

    return '';
  }

  isFileUploadDataset(button: ButtonInfo): boolean {
    return button.btnRoute === '/home/data_management/upload_files/';
  }

  downloadDataset(button: ButtonInfo) {
    this.dataManagementService.downloadDatasetFiles(button.dataName).subscribe({
      next: (response) => {
        const blob = response.body as Blob;
        const disposition = response.headers.get('Content-Disposition') || '';
        const match = disposition.match(/filename="?([^";]+)"?/);
        const fallbackName =
          blob.type === 'application/zip'
            ? `${button.dataName}.zip`
            : `${button.dataName}.xlsx`;
        this.dataManagementService.saveCsvFile(
          blob,
          match ? match[1] : fallbackName
        );
      },
      error: async (error: any) => {
        let message = 'No downloadable files for this dataset';
        try {
          const text = await error.error.text();
          message = JSON.parse(text).message || message;
        } catch (e) {
        }
        this.snackBar.openSnackBar(message, 'Close', 'error-snackbar');
      },
    });
  }

  deleteDataset(dataName: string, dataRoute: string, index: number) {
    if (dataRoute === '/home/data_management/upload_files/') {
      this.deleteExcelDataset(dataName, index);
    } else {
      this.deleteStandardDataset(dataName, index);
    }
  }

  deleteStandardDataset(name: string, index: number) {
    const dialogRef = this.dialog.open(ShowDialogsComponent, {
      data: {
        title: 'Delete data source',
        message: `Delete '${name}'? This permanently removes the data source and its files.`,
        confirmText: 'Delete',
        danger: true,
      },
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dataManagementService.deleteDataManagemententry(name).subscribe({
          next: (response: any) => {
            this.buttonsKeys.splice(index, 1);
            this.snackBar.openSnackBar(
              response.msg,
              'Close',
              'success-snackbar'
            );
          },
          error: (error: any) => {
            this.snackBar.openSnackBar(
              error?.msg || error?.error?.msg,
              'Close',
              'error-snackbar'
            );
          },
        });
      }
    });
  }

  deleteExcelDataset(name: string, index: number) {
    const dialogRef = this.dialog.open(ShowDialogsComponent, {
      data: {
        title: 'Delete data source',
        message: `Delete '${name}'? This permanently removes the data source and its files.`,
        confirmText: 'Delete',
        danger: true,
      },
      disableClose: false,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.dataManagementService.deleteUploadedFiles(name).subscribe({
          next: (response: any) => {
            this.buttonsKeys.splice(index, 1);
            this.snackBar.openSnackBar(
              response.message,
              'Close',
              'success-snackbar'
            );
          },
          error: (error: any) => {
            this.snackBar.openSnackBar(
              error.error.msg,
              'Close',
              'error-snackbar'
            );
          },
        });
      }
    });
  }
}
